import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import StudentHubHero, { STUDENT_LEVELS } from '../components/StudentHubHeroV2';
import PopularThisWeek from '../components/PopularThisWeek';
import ProfileTypePicker, { getProfileRoute } from '../components/ProfileTypePicker';
import { useHubAuthGate } from '../components/HubAuthGate';
import { GraduationCap, Building2, BookOpen, Wrench, User, X, Check } from 'lucide-react';
import {
  BadgeModal,
  XPToast,
  LevelUpModal,
  StudentBadgesRow,
} from '../components/StudentGamification';
import {
  loadGamificationData,
  recordDailyVisit,
} from '../services/studentGamification';
import { trackStudentHubOpened, trackLevelSelected, trackCategoryClicked } from '../utils/studentHubAnalytics';
import { isFeatureEnabled, getBrowseRoute } from '../config/studentHubFeatureFlags';
import '../components/StudentHubHeroV2.css';
import '../components/PopularThisWeek.css';
import './StudentHub.css';

// LocalStorage key for student level
const STUDENT_LEVEL_KEY = 'studentHub.level';
const LEVEL_MODAL_DISMISSED_KEY = 'studentHub.levelModalDismissed';

// Icon map for academic level options
const LEVEL_ICONS = {
  secondary: GraduationCap,
  university: Building2,
  postgrad: BookOpen,
  vocational: Wrench,
  other: User,
};

/**
 * Student Hub Landing Page V2
 * - NO search bar (search lives in category pages)
 * - Level Selector with backend sync
 * - 4-card category grid (Courses & Admissions disabled)
 */
export default function StudentHubLandingV2() {
  const { t, i18n } = useTranslation();
  const { user, token, isReady, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // RTL detection
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl' || isArabic;
  const lang = isRTL ? 'ar' : 'en';
  
  // Hub Auth Gate - shows login modal for unauthenticated users
  const { AuthModal, WelcomeModal } = useHubAuthGate('student-hub', lang);
  
  // ========== LEVEL SELECTOR STATE ==========
  // Read cached level IMMEDIATELY on mount (no flicker)
  const [selectedLevel, setSelectedLevel] = useState(() => {
    try {
      return localStorage.getItem(STUDENT_LEVEL_KEY) 
        || localStorage.getItem('student_academic_level') 
        || null;
    } catch {
      return null;
    }
  });
  
  // Level modal state (only show if conditions met)
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [modalConfirmedLevel, setModalConfirmedLevel] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const levelModalShownRef = useRef(false);
  const backendFetchedRef = useRef(false);
  
  // ========== PROFILE TYPE PICKER STATE ==========
  const { 
    needsProfileType, 
    setProfileType: authSetProfileType, 
    dismissProfileTypePicker 
  } = useAuth();
  
  const [showProfilePicker, setShowProfilePicker] = useState(false);
  const pickerShownRef = useRef(false);
  const dismissedThisSessionRef = useRef(false);
  
  // ========== GAMIFICATION STATE ==========
  const [gamificationData, setGamificationData] = useState(null);
  const [xpToast, setXpToast] = useState(null);
  const [levelUpModal, setLevelUpModal] = useState(null);
  const [selectedBadge, setSelectedBadge] = useState(null);
  
  // ========== LEVEL SELECTOR LOGIC ==========
  
  // Fetch backend level and reconcile
  useEffect(() => {
    if (!isReady || !isLoggedIn || !token || backendFetchedRef.current) return;
    
    const fetchAndReconcile = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/student-level`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!res.ok) return;
        
        const data = await res.json();
        const backendLevel = data.student_level;
        const localLevel = localStorage.getItem(STUDENT_LEVEL_KEY);
        
        backendFetchedRef.current = true;
        
        // Reconcile algorithm
        if (backendLevel && backendLevel !== localLevel) {
          // Backend has a level and it differs - use backend
          setSelectedLevel(backendLevel);
          localStorage.setItem(STUDENT_LEVEL_KEY, backendLevel);
        } else if (!backendLevel && localLevel) {
          // Backend missing but local exists - sync to backend (fire-and-forget)
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/student-level`, {
            method: 'PATCH',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ student_level: localLevel })
          }).catch(() => {});
        } else if (!backendLevel && !localLevel) {
          // Both missing - show modal (once per lifetime, not per session)
          const dismissed = localStorage.getItem(LEVEL_MODAL_DISMISSED_KEY);
          if (!dismissed && !levelModalShownRef.current) {
            levelModalShownRef.current = true;
            setShowLevelModal(true);
          }
        }
      } catch (e) {
        console.error('Failed to fetch student level:', e);
      }
    };
    
    fetchAndReconcile();
  }, [isReady, isLoggedIn, token]);
  
  // Handle level selection
  const handleLevelChange = useCallback(async (level) => {
    // Update UI immediately
    setSelectedLevel(level);
    localStorage.setItem(STUDENT_LEVEL_KEY, level);
    localStorage.setItem('student_academic_level', level);
    
    // Close modal if open, with brief confirmation delay
    if (showLevelModal) {
      setModalConfirmedLevel(level);
      setTimeout(() => {
        setShowLevelModal(false);
        setModalConfirmedLevel(null);
        setIsEditMode(false);
        localStorage.setItem(LEVEL_MODAL_DISMISSED_KEY, '1');
      }, 800);
    }
    
    // Show confirmation toast
    const levelLabel = t(`shl.${level}`);
    if (isEditMode) {
      toast.success(t('shl.levelUpdated'), { duration: 2000 });
    } else {
      toast.success(t('shl.levelSelected', { level: levelLabel }), { duration: 2000 });
    }
    
    // Analytics: track level selection
    trackLevelSelected(level, isEditMode);
    
    // Sync to backend (fire-and-forget)
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/student-level`, {
          method: 'PATCH',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ student_level: level })
        });
      } catch (e) {
        console.error('Failed to sync level:', e);
      }
    }
  }, [token, showLevelModal, isEditMode, t]);
  
  // Handle level modal dismiss
  const handleLevelModalDismiss = useCallback(() => {
    setShowLevelModal(false);
    setIsEditMode(false);
    localStorage.setItem(LEVEL_MODAL_DISMISSED_KEY, '1');
  }, []);
  
  // ========== PROFILE PICKER LOGIC ==========
  
  useEffect(() => {
    if (!isReady || !isLoggedIn || !needsProfileType) return;
    if (pickerShownRef.current || dismissedThisSessionRef.current) return;
    
    const sessionDismissed = sessionStorage.getItem('wassla_profile_picker_dismissed_session');
    if (sessionDismissed) {
      dismissedThisSessionRef.current = true;
      return;
    }
    
    const timer = setTimeout(() => {
      if (!pickerShownRef.current && !dismissedThisSessionRef.current) {
        pickerShownRef.current = true;
        setShowProfilePicker(true);
      }
    }, 400);
    
    return () => clearTimeout(timer);
  }, [isReady, isLoggedIn, needsProfileType]);
  
  const handleProfileTypeSelect = useCallback(async (type) => {
    const success = await authSetProfileType(type);
    
    if (success) {
      localStorage.setItem('wassla_profile_type', type);
      localStorage.setItem('wassla_profile_type_set_at', new Date().toISOString());
      localStorage.setItem('hasCompletedProfileSelection', 'true');
      
      toast.success(
        t('home.yourPreferenceHasBeenSaved'),
        { duration: 3000 }
      );
      
      setShowProfilePicker(false);
      
      if (type !== 'student') {
        const targetRoute = getProfileRoute(type, true);
        if (targetRoute !== '/student-hub') {
          setTimeout(() => navigate(targetRoute, { replace: true }), 300);
        }
      }
    }
  }, [isRTL, navigate, authSetProfileType]);
  
  const handlePickerClose = useCallback(async () => {
    setShowProfilePicker(false);
    dismissedThisSessionRef.current = true;
    sessionStorage.setItem('wassla_profile_picker_dismissed_session', 'true');
    await dismissProfileTypePicker();
  }, [dismissProfileTypePicker]);
  
  // ========== GAMIFICATION LOGIC ==========
  
  useEffect(() => {
    const result = recordDailyVisit();
    setGamificationData(result.data);
    
    if (result.xpAwarded > 0) {
      setXpToast({ xp: result.xpAwarded, message: t('studhub.dailyVisit') });
    }
    
    if (result.newLevel) {
      setTimeout(() => {
        setLevelUpModal({ level: result.level });
      }, 500);
    }
  }, [isRTL]);
  
  // ========== ANALYTICS: Track page open ==========
  useEffect(() => {
    trackStudentHubOpened(selectedLevel);
    // Fire once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // ========== NAVIGATION ==========
  
  const handleBack = useCallback(() => {
    // Pop real history when it exists; home only for deep links.
    if (hasInAppHistory()) return navigate(-1);
    navigate('/', { replace: true });
  }, [navigate]);
  
  const handleCategoryClick = useCallback((categorySlug, route) => {
    // Fire analytics for this category click
    trackCategoryClicked(categorySlug);
    
    // Use feature-flag canonical route if available, else fall back to card route
    const canonicalRoute = getBrowseRoute(categorySlug) || route;
    const levelParam = selectedLevel ? `?level=${selectedLevel}` : '';
    navigate(`${canonicalRoute}${levelParam}`, { state: { from: '/student-hub' } });
  }, [navigate, selectedLevel]);
  
  return (
    <div className="student-hub-page student-hub-landing" dir={isRTL ? 'rtl' : 'ltr'} data-testid="student-hub">
      {/* Hub Auth Gate - Login/Signup Modal for unauthenticated users */}
      <AuthModal />
      <WelcomeModal />
      
      {/* Profile Type Picker */}
      <ProfileTypePicker
        isOpen={showProfilePicker}
        onClose={handlePickerClose}
        onSelect={handleProfileTypeSelect}
      />
      
      {/* Level Selection Modal (first-visit or edit mode) */}
      {showLevelModal && (
        <div className="level-modal-overlay" data-testid="level-modal" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="level-modal-content" data-testid="level-modal-content">
            {/* Close button */}
            <button
              className="level-modal-close"
              onClick={handleLevelModalDismiss}
              data-testid="level-modal-close"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <h2 data-testid="level-modal-title">
              {isEditMode ? t('shl.changeAcademicLevel') : t('shl.selectYourAcademicLevel')}
            </h2>
            <p className="level-modal-desc" data-testid="level-modal-desc">
              {t('shl.wellPersonalizeContentBasedOn')}
            </p>

            <div className="level-modal-options" data-testid="level-modal-options">
              {STUDENT_LEVELS.map((level) => {
                const Icon = LEVEL_ICONS[level.key] || User;
                const isConfirmed = modalConfirmedLevel === level.key;
                const isCurrentLevel = isEditMode && !modalConfirmedLevel && selectedLevel === level.key;
                return (
                  <button
                    key={level.key}
                    className={`level-modal-option${isConfirmed ? ' confirmed' : ''}${isCurrentLevel ? ' current' : ''}`}
                    onClick={() => handleLevelChange(level.key)}
                    data-testid={`level-option-${level.key}`}
                    disabled={!!modalConfirmedLevel}
                  >
                    <span className="level-option-icon">
                      {isConfirmed ? <Check size={20} /> : <Icon size={20} />}
                    </span>
                    <span className="level-option-text">
                      <span className="level-option-label">{t(`shl.${level.key}`)}</span>
                      {level.key === 'vocational' && (
                        <span className="level-option-hint">{t('shl.vocationalHint')}</span>
                      )}
                    </span>
                    {isCurrentLevel && (
                      <span className="level-option-current-badge">{t('shl.yourLevel')}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <button 
              className="level-modal-dismiss"
              onClick={handleLevelModalDismiss}
              data-testid="level-modal-skip"
            >
              {isEditMode ? t('close') : t('shl.later')}
            </button>
          </div>
        </div>
      )}
      
      {/* Gamification Toasts & Modals */}
      {xpToast && (
        <XPToast 
          xp={xpToast.xp} 
          message={xpToast.message} 
          onClose={() => setXpToast(null)} 
        />
      )}
      {levelUpModal && (
        <LevelUpModal 
          level={levelUpModal.level} 
          onClose={() => setLevelUpModal(null)} 
        />
      )}
      {selectedBadge && (
        <BadgeModal 
          badge={selectedBadge} 
          onClose={() => setSelectedBadge(null)}
          lang={lang}
        />
      )}
      
      {/* Hero with Level Selector and 6-Card Grid */}
      <StudentHubHero
        lang={lang}
        onBack={handleBack}
        onCategoryClick={handleCategoryClick}
        selectedLevel={selectedLevel}
        onLevelChange={handleLevelChange}
      />
      
      {/* Badges Row */}
      <StudentBadgesRow onBadgeClick={setSelectedBadge} />
      
      {/* Popular This Week Section */}
      <PopularThisWeek lang={lang} />
    </div>
  );
}
